<script>
import {
  CdrButton, IconXSm, CdrLink,
} from '@rei/cedar';

export default {
  name: 'ProductBanner',
  components: {
    CdrButton,
    IconXSm,
    CdrLink,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    showProductBanner: true,
    isPageVisible: true,
  }),
  computed: {
    productList() {
      return this.data.searchProductList;
    },
    ctaText() {
      return this.data.ctaText;
    },
    title() {
      return this.data.searchLink.text;
    },
    searchLink() {
      return this.data.searchLink.url;
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleClick(evt) {
      const { currentTarget: e } = evt;
      const siteId = 'expert advice';
      const pageName = 'article sticky-banner';
      const contentDetails = e.dataset.analyticsId || '';

      const data = {
        linkName: `${siteId}:${pageName}_${contentDetails}`,
      };

      window.metrics.link(data);
    },
    handleScroll() {
      const eaPage = document.getElementById('ea_details-page');
      const windowHeight = window.innerHeight;
      const { bottom: eaPageBottomRect } = eaPage.getBoundingClientRect();

      this.isPageVisible = eaPageBottomRect > windowHeight;
    },
  },
};
</script>

<template>
  <section
    v-if="showProductBanner"
    id="ea-product-search-banner"
    :class="{
      'product-banner': isPageVisible,
      'product-banner product-banner--hidden': !isPageVisible,
    }"
  >
    <div :class="'product-banner__img-container'">
      <CdrLink
        v-for="(product, idx) in productList"
        :key="idx"
        :href="product.url"
        :data-analytics-id="`click_${data.searchLink.text}`"
        data-test-id="product-link"
        :modifier="'standalone'"
        @click="handleClick"
      >
        <template #default>
          <img
            :src="product.image_url"
            :alt="`Product image #${idx+1}`"
          >
        </template>
      </CdrLink>
    </div>

    <div :class="'product-banner__text-container'">
      <p
        v-if="ctaText"
        :class="'product-banner__cta'"
        data-test-id="cta-text"
      >
        {{ ctaText }}
      </p>
      <CdrLink
        v-if="title"
        :modifier="'standalone'"
        :class="'product-banner__title'"
        :href="searchLink"
        :data-analytics-id="`click_${title}`"
        data-test-id="category-link"
        @click="handleClick"
      >
        <template #default>
          {{ title }}
        </template>
      </CdrLink>
    </div>

    <cdr-button
      :icon-only="true"
      :with-background="false"
      :full-width="false"
      :class="'product-banner__x-icon'"
      data-test-id="close-button"
      aria-label="Close product banner"
      modifier="primary"
      size="small"
      @click="showProductBanner = false"
    >
      <template #icon>
        <icon-x-sm
          alt="Close"
        />
      </template>
    </cdr-button>
  </section>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';
@import '../../../../style/common/editor-styles.scss';

.product-banner {
  @include cdr-text-body-400;

  background-color: $cdr-color-background-primary;
  border: 1px solid $cdr-color-border-primary;
  box-shadow: 0px 0px 4px 0px $cdr-color-border-button-dark-hover;
  bottom: 0;
  display: flex;
  gap: 20px;
  height: 80px;
  left: 0;
  padding-left: 50px;
  position: fixed;
  transition: all $cdr-duration-2-x $cdr-timing-function-ease-out 0s;
  width: 100%;
  z-index: 100;

  &--hidden {
    bottom: -100px;
  }

  @include cdr-sm-mq-down {
    padding-left: 15px;
    height: 100px;

    &__img-container a:not(:nth-child(1)) {
      display: none;
    }
  }

  &__img-container {
    align-items: center;
    display: flex;
    gap: 8px;

    img {
      width: 80px;
      height: 80px;
      object-fit: contain;
    }
  }

  &__text-container {
    align-content: center;
  }

  &__cta {
    @include cdr-text-body-strong-300;
    font-size: 12px;
    text-transform: uppercase;
  }

  &__title {
    @include cdr-text-heading-serif-300;
    color: inherit;
    text-decoration: none;
  }

  button.product-banner__x-icon {
    align-self: flex-start;
    display: flex;
    margin-left: auto;
  }
}
</style>
